import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createEquipment, deleteEquipment, getEquipment, listEquipment, setEquipmentArchived, updateEquipment } from '../features/equipment/api';
import type { EquipmentRow } from '../lib/types';
import { EQUIPMENT_CATEGORIES, EQUIPMENT_CLASSES, label, type EquipmentCategory, type EquipmentClass } from '../lib/vocab';
import { slugifyEquipmentId } from '../lib/slugify';
import { validateEquipmentId, validateName } from '../lib/validators';
import { IMAGE_TARGETS } from '../lib/image';
import { releaseStaged, stageImage, type StagedImage } from '../lib/staging';
import { PreviewModal } from '../features/preview/PreviewModal';
import { equipmentPhotoPath } from '../lib/storagePaths';
import { deleteMedia, mediaUrl, uploadMedia } from '../lib/storage';
import { friendlyErrorMessage } from '../lib/errors';
import { useToast } from '../components/Toast';
import { Button } from '../components/Button';
import { SelectField, TextField } from '../components/Field';
import { Dropzone } from '../components/Dropzone';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Spinner } from '../components/Spinner';

export function EquipmentEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [existingIds, setExistingIds] = useState<string[]>([]);
  const [row, setRow] = useState<EquipmentRow | null>(null);

  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [nameEn, setNameEn] = useState('');
  const [nameHe, setNameHe] = useState('');
  const [category, setCategory] = useState<EquipmentCategory>('other');
  const [equipClass, setEquipClass] = useState<EquipmentClass>('other');
  const [sort, setSort] = useState(0);
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [viewing, setViewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    listEquipment().then((all) => setExistingIds(all.map((e) => e.id)));
    if (!isNew && id) {
      getEquipment(id).then((r) => {
        if (r) {
          setRow(r);
          setSlug(r.id);
          setNameEn(r.name_en);
          setNameHe(r.name_he);
          setCategory(r.category);
          setEquipClass(r.class);
          setSort(r.sort);
          setPhotoPath(r.photo_path);
        }
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isNew && !slugTouched) {
      setSlug(slugifyEquipmentId(nameEn || '', existingIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameEn, isNew, slugTouched]);

  async function handlePhoto(file: File) {
    setCompressing(true);
    try {
      setStaged(await stageImage(file, IMAGE_TARGETS.equipment));
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setCompressing(false);
    }
  }

  function cancelPhoto() {
    releaseStaged(staged);
    setStaged(null);
  }

  async function confirmPhoto() {
    if (!staged) return;
    setUploading(true);
    try {
      const path = equipmentPhotoPath(slug || 'tmp', staged.ext);
      await uploadMedia(path, staged.blob, staged.mime);
      const oldPath = photoPath;
      setPhotoPath(path);
      cancelPhoto();
      if (oldPath) await deleteMedia([oldPath]);
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    const nextErrors: Record<string, string | null> = {
      slug: validateEquipmentId(slug),
      nameEn: validateName(nameEn, 'English name'),
      nameHe: validateName(nameHe, 'Hebrew name'),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSaving(true);
    try {
      if (isNew) {
        await createEquipment({ id: slug, name_en: nameEn, name_he: nameHe, category, class: equipClass, photo_path: photoPath, sort });
        toast.show('Equipment created', 'success');
        navigate(`/equipment/${slug}`);
      } else if (id) {
        await updateEquipment(id, { name_en: nameEn, name_he: nameHe, category, class: equipClass, photo_path: photoPath, sort });
        toast.show('Saved', 'success');
      }
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleArchive() {
    if (!row) return;
    try {
      await setEquipmentArchived(row.id, !row.archived_at);
      setRow({ ...row, archived_at: row.archived_at ? null : new Date().toISOString() });
      toast.show(row.archived_at ? 'Unarchived' : 'Archived', 'success');
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  async function handleDelete() {
    if (!row) return;
    setConfirmDelete(false);
    try {
      await deleteEquipment(row.id);
      if (row.photo_path) await deleteMedia([row.photo_path]);
      toast.show('Deleted', 'success');
      navigate('/equipment');
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">{isNew ? 'Add equipment' : nameEn || row?.id}</h1>
        {!isNew && row ? (
          <div className="row" style={{ flex: 'initial' }}>
            <Button tone="neutral" onClick={handleToggleArchive} style={{ flex: 'initial' }}>
              {row.archived_at ? 'Unarchive' : 'Archive'}
            </Button>
            <Button tone="danger" onClick={() => setConfirmDelete(true)} style={{ flex: 'initial' }}>
              Delete
            </Button>
          </div>
        ) : null}
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        <TextField
          label="Id (slug)"
          value={slug}
          disabled={!isNew}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value.toLowerCase());
          }}
          error={errors.slug}
          hint={isNew ? 'Auto-generated from the English name; locked after create.' : 'Locked after create.'}
        />
        <div className="row">
          <TextField label="Name (English)" value={nameEn} onChange={(e) => setNameEn(e.target.value)} error={errors.nameEn} />
          <TextField label="Name (Hebrew)" dirAuto value={nameHe} onChange={(e) => setNameHe(e.target.value)} error={errors.nameHe} />
        </div>
        <div className="row">
          <SelectField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as EquipmentCategory)}
            options={EQUIPMENT_CATEGORIES.map((c) => ({ value: c, label: label(c) }))}
          />
          <SelectField
            label="Class"
            value={equipClass}
            onChange={(e) => setEquipClass(e.target.value as EquipmentClass)}
            options={EQUIPMENT_CLASSES.map((c) => ({ value: c, label: label(c) }))}
            hint="Maps this item onto the coarse exercises.equipment vocabulary."
          />
        </div>
        <TextField
          label="Sort"
          type="number"
          value={sort}
          onChange={(e) => setSort(Number(e.target.value))}
          style={{ maxWidth: 120 }}
        />

        <div className="field">
          <label>Photo</label>
          {photoPath ? (
            <img
              src={mediaUrl(photoPath) ?? undefined}
              alt=""
              onClick={() => setViewing(true)}
              style={{ width: 160, aspectRatio: 1, objectFit: 'cover', borderRadius: 14, marginBottom: 8, cursor: 'zoom-in' }}
            />
          ) : null}
          <Dropzone
            accept="image/jpeg,image/png,image/webp,image/heic"
            label={compressing ? 'Compressing…' : 'Drop a photo, or click to choose'}
            onFile={handlePhoto}
            disabled={compressing || uploading}
          />
          {photoPath ? (
            <Button tone="tonal" size="sm" onClick={() => setViewing(true)} style={{ marginTop: 8, width: 'fit-content' }}>
              Preview
            </Button>
          ) : null}
        </div>
        {staged ? (
          <PreviewModal
            subject={{ kind: 'equipment', name: nameEn || 'Equipment', photoUrl: staged.url }}
            stats={{ originalBytes: staged.originalBytes, finalBytes: staged.blob.size }}
            onConfirm={confirmPhoto}
            confirming={uploading}
            onClose={cancelPhoto}
          />
        ) : null}
        {viewing && photoPath ? (
          <PreviewModal subject={{ kind: 'equipment', name: nameEn || 'Equipment', photoUrl: mediaUrl(photoPath) ?? '' }} onClose={() => setViewing(false)} />
        ) : null}

        <Button onClick={handleSave} disabled={saving} style={{ width: '100%' }}>
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete equipment"
        message={`Delete "${nameEn}"? This fails with an error if any exercise uses it -- archive it instead if so.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
